import { useState } from 'react';
import { formatarCno } from '@raiox/domain';
import { trpc } from '../lib/trpc.js';
import { paraApi, useFiltro } from '../estado/filtros.js';
import { Carregando, Erro, Vazio } from '../componentes/Estados.js';
import { data, inteiro, moeda } from '../lib/formato.js';
import { nomeArquivo, urlExportacao } from '../lib/exportar.js';
import { ROTULO_SEVERIDADE, type Severidade } from '../lib/tipos.js';

const POR_PAGINA = 50;
const SEVERIDADES: Severidade[] = ['crit', 'warn', 'info'];

export function Divergencias() {
  const { filtro } = useFiltro();
  const [severidade, setSeveridade] = useState<Severidade | undefined>(undefined);
  const [codigo, setCodigo] = useState<string | undefined>(undefined);
  const [pagina, setPagina] = useState(0);

  const filtroApi = paraApi(filtro);
  const agrupado = trpc.alertas.porCodigo.useQuery({ filtro: filtroApi });
  const lista = trpc.alertas.listar.useQuery({
    filtro: filtroApi,
    severidade,
    codigo,
    pagina,
    porPagina: POR_PAGINA,
  });

  const grupos = agrupado.data ?? [];
  const contagem = (s: Severidade): number =>
    grupos.filter((g) => g.severidade === s).reduce((acc, g) => acc + g.total, 0);
  const totalGeral = grupos.reduce((acc, g) => acc + g.total, 0);

  function trocarSeveridade(nova: Severidade | undefined) {
    setSeveridade(nova);
    setPagina(0);
  }

  function trocarCodigo(novo: string | undefined) {
    setCodigo((atual) => (atual === novo ? undefined : novo));
    setPagina(0);
  }

  const total = lista.data?.total ?? 0;
  const itens = lista.data?.itens ?? [];
  const ultimaPagina = Math.max(0, Math.ceil(total / POR_PAGINA) - 1);

  return (
    <section className="panel">
      <div className="toolbar">
        <div className="filters">
          <button
            type="button"
            className="fchip"
            aria-pressed={severidade === undefined}
            onClick={() => trocarSeveridade(undefined)}
          >
            Todas ({inteiro(totalGeral)})
          </button>
          {SEVERIDADES.map((s) => (
            <button
              key={s}
              type="button"
              className="fchip"
              aria-pressed={severidade === s}
              onClick={() => trocarSeveridade(s)}
            >
              {ROTULO_SEVERIDADE[s]} ({inteiro(contagem(s))})
            </button>
          ))}
        </div>
        <div className="search" />
        <a
          className="btn sm"
          href={urlExportacao('divergencias', filtro)}
          download={nomeArquivo('divergencias')}
        >
          Exportar divergências
        </a>
      </div>

      {agrupado.error ? <Erro erro={agrupado.error} aoTentarDeNovo={() => void agrupado.refetch()} /> : null}

      {grupos.length > 0 ? (
        <>
          <div className="eyebrow">Por verificação</div>
          <div className="tablewrap">
            <table>
              <thead>
                <tr>
                  <th>Verificação</th>
                  <th>Severidade</th>
                  <th className="r">Ocorrências</th>
                  <th className="r">Valor dos documentos</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {grupos.map((g) => (
                  <tr key={`${g.codigo}-${g.severidade}`}>
                    <td className="strong">{g.codigo}</td>
                    <td>
                      <span className={`badge ${g.severidade}`}>{ROTULO_SEVERIDADE[g.severidade]}</span>
                    </td>
                    <td className="num r">{inteiro(g.total)}</td>
                    <td className="num r">{moeda(g.valorExposto)}</td>
                    <td className="r">
                      <button
                        type="button"
                        className="fchip"
                        aria-pressed={codigo === g.codigo}
                        onClick={() => trocarCodigo(g.codigo)}
                      >
                        {codigo === g.codigo ? 'Limpar' : 'Filtrar'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}

      <div className="eyebrow">
        Ocorrências
        {codigo ? ` · ${codigo}` : ''}
      </div>

      {lista.error ? <Erro erro={lista.error} aoTentarDeNovo={() => void lista.refetch()} /> : null}
      {lista.isPending ? <Carregando linhas={5} rotulo="Carregando as divergências" /> : null}

      {!lista.isPending && !lista.error && itens.length === 0 ? (
        <Vazio titulo="Nada a apontar">
          Os documentos desta seleção passaram por todas as verificações configuradas.
        </Vazio>
      ) : null}

      {itens.length > 0 ? (
        <>
          <div className="alist">
            {itens.map((a) => (
              <div
                key={a.id}
                className={`alert ${a.severidade === 'crit' ? 'crit' : a.severidade === 'info' ? 'info' : ''}`}
              >
                <div className="top">
                  <h3>
                    <span className={`dot ${a.severidade}`} /> {a.codigo}
                  </h3>
                  <span className="badge tipo">
                    {a.tipo} {a.numero}
                  </span>
                </div>
                <p>{a.mensagem}</p>
                <div className="ref">
                  <span>{a.emitenteNome}</span>
                  <span className="mono">{data(a.dataEmissao)}</span>
                  <span className="mono">{moeda(a.valorTotal)}</span>
                  {a.cnos.length > 0 ? <span className="mono">CNO {formatarCno(a.cnos[0] ?? '')}</span> : null}
                </div>
              </div>
            ))}
          </div>

          <div className="pag">
            <span>
              {inteiro(total)} ocorrência(s) · página {inteiro(pagina + 1)} de {inteiro(ultimaPagina + 1)}
            </span>
            <button
              type="button"
              className="btn sm"
              disabled={pagina === 0 || lista.isFetching}
              onClick={() => setPagina((p) => Math.max(0, p - 1))}
            >
              Anterior
            </button>
            <button
              type="button"
              className="btn sm"
              disabled={pagina >= ultimaPagina || lista.isFetching}
              onClick={() => setPagina((p) => Math.min(ultimaPagina, p + 1))}
            >
              Próxima
            </button>
          </div>
        </>
      ) : null}
    </section>
  );
}
